import { z } from 'zod';
import { complianceDecisionsSchema } from '../requirements/compliance.js';
import { communicationSchema } from '../mail/communication.js';
import { screeningRecurrencesSchema } from '../requirements/screening-recurrence.js';
import { defaultCompetencyRoleForms, defaultCompetencyRequirementMappings, defaultScreeningRecurrence } from '../catalog/settings.seed.js';
import { stepInstanceSchema } from '../steps/definitions.js';
import { fileIdSchema } from '../files/stored-file.js';
import { evidenceUploadIdSchema } from '../credentials/evidence-details.js';
import { agencyCalendarDate } from '../time/agency-dates.js';
import { servicePlacesSchema } from '../people/details.js';
import { servicePlacesWords } from '../places/service-place-words.js';
import { fileCategorySchema, isSegregatedCategory } from '../files/categories.js';
import { competencyRoleMappingsSchema, competencyRequirementMappingsSchema, } from '../credentials/competency.js';
const text = z.string().trim().max(4000);
const title = z.string().trim().min(1).max(200);
/**
 * How many interviewers the agency's pick list may hold.
 *
 * One constant, read by the settings schema's cap AND by the refusal the add command answers with,
 * so the two cannot drift into a state where the server accepts an add that the schema then rejects
 * — which would fail as a validation error on the whole settings document rather than as a sentence
 * about the list.
 */
export const MAX_AGENCY_INTERVIEWERS = 50;
const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((v) => {
    const parsed = new Date(`${v}T00:00:00Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === v;
}, 'Use a valid calendar date');
const httpsUrl = z.string().url().refine((v) => new URL(v).protocol === 'https:', 'Use an HTTPS link');
export const personnelEvidenceUrlSchema = httpsUrl.refine((v) => new URL(v).hostname.endsWith('.sharepoint.com'), 'Use the filed SharePoint document link');
/** The employment types a NEW offer may carry. Stored as these labels, so every text rendering reads naturally. */
export const offerEmploymentTypes = ['Hourly', 'Salary', 'PRN'];
export const offerTermsSchema = z.object({
    target: fileCategorySchema.default('employee-records'),
    position: title,
    department: title,
    employmentClassification: z.enum(['w2', '1099']),
    /**
     * READ WIDE, WRITE NARROW (#348, Paul Rios 2026-09-28). A signed offer keeps whatever employment
     * type it was signed with, so this stays free text on read and an old record never stops parsing.
     * New and edited offers are held to `offerEmploymentTypes` by `writableOfferTermsSchema`, the same
     * pattern as `writableHrWorkflowSettingsSchema`. `employeeAssignmentSchema` is not narrowed: it
     * inherits what the signed offer carried.
     */
    employmentType: title,
    /**
     * The service area in words. An offer written before #764 holds only this, typed by hand. A new or
     * edited offer also holds `servicePlaces` (picked from our own list, as on the Roster) and this text is
     * then derived from them by `writableOfferTermsSchema`, so the letter, the portal and the activation
     * dialog never read two different areas.
     */
    serviceArea: title,
    /** Optional on read: an old offer has none, and the activation dialog then shows `serviceArea` as a hint only. */
    servicePlaces: servicePlacesSchema.optional(),
    proposedStartDate: date,
    acceptanceDeadline: z.string().datetime(),
    compensation: z.array(z.object({
        description: title,
        amount: z.number().finite().nonnegative().max(10_000_000),
        unit: z.enum(['hour', 'visit', 'year', 'day', 'mile', 'other']),
        details: text.default(''),
    }).strict()).min(1).max(25),
    /**
     * The approved offer letter, uploaded by HR through quarantine (#188, #260). The id only —
     * the bytes are never in the command. `issue-offer` refuses until its scan is `clean`, so a
     * letter nobody has checked can never reach a candidate.
     */
    letterUploadId: evidenceUploadIdSchema.optional(),
    /**
     * OPTIONAL since #260: HR uploads the approved letter instead of retyping it. Drafts written
     * before that change still carry their typed wording, which is why this is optional rather
     * than removed — an offer that exists must keep parsing.
     */
    termsText: z.string().trim().min(1).max(16000).optional(),
}).strict();
/**
 * THE TERMS THAT MAY BE SAVED, as opposed to the terms that may be read.
 *
 * TWO rules, both of them about what may be SAVED.
 *
 * `target` decides where the candidate's SIGNED offer PDF is filed. It was a bare
 * `fileCategorySchema` and `OfferEditor` offered all eleven categories, so a signed offer could be
 * committed into the segregated I-9 or health store in one dropdown pick — onto the ADA retention
 * clock and behind `files.restricted.read`.
 *
 * #288 also gave a second reason: that `isSegregatedInstance` could not classify the row at all,
 * because it read `config.target` while an `hr-record` carries `config.fileCategory`. That was true
 * and is no longer — the classifier reads both keys through `configFileCategory` (#292). The rule
 * here does NOT depend on it: an offer letter is not an I-9 or a health record whether or not its
 * audience can be computed, so the narrowing below stands on its own.
 *
 * The paper path already refused this at the point of filing; the PORTAL path had no guard at all,
 * and narrowing `defaultOfferTarget` did not reach it — that setting is only this field's initial
 * value. So the two paths disagreed about the same rule, which is worse than either answer.
 *
 * An offer must also rest on something: the uploaded letter (#260) or typed wording. Since the
 * frame stopped asking HR to retype the terms, `termsText` is optional, and "neither" became
 * expressible for the first time.
 *
 * Reads stay wide, for the same reason `hrWorkflowSettingsSchema` does: a stored offer that already
 * carries a segregated target, or only its pre-#260 wording, must still parse, or the whole
 * record — and the person's workflow with it — fails to load.
 */
export const writableOfferTermsSchema = offerTermsSchema
    .refine((terms) => offerEmploymentTypes.includes(terms.employmentType), { path: ['employmentType'], message: 'Choose Hourly, Salary or PRN.' })
    .refine((terms) => !isSegregatedCategory(terms.target), { path: ['target'], message: 'A signed offer cannot be filed into a segregated store.' })
    .refine((terms) => terms.letterUploadId !== undefined || terms.termsText !== undefined, { path: ['letterUploadId'], message: 'Upload the approved offer letter.' })
    // A new or edited offer names its service areas as places of our own list (#764), so they can carry over to the
    // employee record. Reads stay wide: an offer stored with only a typed area still parses (`offerTermsSchema`), and
    // editing one needs a place.
    .refine((terms) => terms.servicePlaces !== undefined && terms.servicePlaces.length > 0, { path: ['servicePlaces'], message: 'Pick at least one service area.' })
    // The words always come from the places, so the two cannot disagree on a saved offer.
    .transform((terms) => ({ ...terms, serviceArea: servicePlacesWords(terms.servicePlaces ?? []) || terms.serviceArea }));
/**
 * The two people who interview candidates today (#341, Paul Rios via Adrian, 2026-09-28).
 *
 * `userId` is null for BOTH, deliberately and not as a placeholder to fill in later from a guess:
 * the id this app stores is the Static Web Apps principal id, which is not the Entra object id, so
 * writing an Entra id here would record the wrong value everywhere it was read. The real id arrives
 * the one way it can — that person signing in and using "Add me".
 */
export const defaultInterviewerOptions = [
    { id: 'khalia-horne', label: 'Khalia Horne', userId: null },
    { id: 'paul-rios', label: 'Paul Rios', userId: null },
];
/** Shaped like `milestoneTypes`, plus the id that makes an interviewer a person rather than a name. */
export const interviewerOptionSchema = z.object({
    id: title, label: title, userId: z.string().min(1).nullable().default(null),
}).strict();
export const hrWorkflowSettingsSchema = z.object({
    complianceDecisions: complianceDecisionsSchema.optional(),
    screeningRecurrence: screeningRecurrencesSchema.optional(),
    /**
     * READ stays wide on purpose. Narrowing this enum made a stored settings document that already
     * carried a segregated target fail to PARSE — taking the whole record with it, so every HR
     * workflow read for that agency would break. That is a worse failure than the one being fixed.
     * The WRITE is narrowed instead (`saveSettingsSchema` below), so the value cannot be newly set,
     * and `fileSignedLetter` still refuses a legacy one at the point of filing.
     */
    defaultOfferTarget: fileCategorySchema.default('employee-records'),
    competencyForms: competencyRoleMappingsSchema.optional(),
    competencyRequirements: competencyRequirementMappingsSchema.optional(),
    defaultOfferResponseDays: z.number().int().min(1).max(365),
    offerTemplates: z.array(z.object({ id: title, label: title, termsText: z.string().trim().min(1).max(16000) }).strict()).max(50),
    emailTemplates: z.array(z.object({ id: title, label: title, subject: title, text: z.string().trim().min(1).max(16000) }).strict()).max(50),
    milestoneTypes: z.array(z.object({ id: title, label: title }).strict()).min(1).max(50),
    /**
     * WHO MAY BE PICKED AS AN INTERVIEWER on the Add candidate form (#341, frame 06b node `289:378`).
     *
     * Agency settings rather than a list in a component, and shaped like `milestoneTypes` above for
     * the same reason that one is: Paul adds a person here and the picker moves, with no deploy.
     *
     * `userId` is the point, as it is on `Interviewer` in `people/person.ts` — Julio's requirement
     * (#258) is that interviewers are people with ids where we have them, so an interview can be
     * linked to an Outlook account later. Nullable because there is still no staff directory: the
     * only path that attaches a real id today is the form's "Add me" button, and a configured entry
     * carries null until a directory can fill it in.
     *
     * `.default()` rather than required, unlike its siblings: this field is NEW, so a settings
     * document written before it exists must still parse — `.strict()` rejects unknown keys, never
     * missing ones.
     *
     * The factory form is habit, not protection, and an earlier version of this note claimed it
     * stopped two parsed documents sharing one array. It does not, and a review showed the test
     * asserting it could not fail: zod re-parses a default through the element schema, so every
     * parse yields fresh objects whether the default is a function or a literal. Kept because a
     * factory is the safer habit when a default is ever read without parsing — but it earns no
     * test, because there is no behaviour to pin.
     */
    interviewers: z.array(interviewerOptionSchema).max(MAX_AGENCY_INTERVIEWERS).default(() => defaultInterviewerOptions.map((row) => ({ ...row }))),
}).strict().superRefine((value, context) => {
    for (const key of ['offerTemplates', 'emailTemplates', 'milestoneTypes', 'interviewers']) {
        if (new Set(value[key].map((row) => row.id)).size !== value[key].length)
            context.addIssue({ code: 'custom', message: 'Each option needs a unique identity', path: [key] });
    }
});
/**
 * WHAT MAY BE SAVED, as opposed to what may be read.
 *
 * An offer letter is never an I-9 or a health record. `defaultOfferTarget` was a bare
 * `fileCategorySchema` whose dropdown listed all eleven categories, so an admin could point signed
 * offers at the restricted account. A runtime guard refused it on ONE of the two paths that read the
 * setting — after HR had uploaded the letter and typed the note, with a message about an agency-wide
 * setting they cannot act on — while `OfferEditor` read the same value with no guard at all.
 *
 * Refusing it here fixes both readers and needs nobody to remember. Reads stay wide so a document
 * written before this still parses.
 */
export const writableHrWorkflowSettingsSchema = hrWorkflowSettingsSchema.refine((settings) => !isSegregatedCategory(settings.defaultOfferTarget), { path: ['defaultOfferTarget'], message: 'Signed offers cannot be filed into a segregated store.' });
/** Starting choices only. HR can publish replacements without changing source code. */
export const defaultHrWorkflowSettings = {
    screeningRecurrence: [...defaultScreeningRecurrence],
    defaultOfferTarget: 'employee-records',
    competencyForms: [...defaultCompetencyRoleForms],
    competencyRequirements: [...defaultCompetencyRequirementMappings],
    defaultOfferResponseDays: 7, offerTemplates: [],
    emailTemplates: [
        { id: 'welcome', label: 'Welcome', subject: 'Welcome to VIGEO', text: 'Hello {{firstName}},\n\nWelcome to VIGEO. Please use the secure portal link from HR to review your next steps.' },
        { id: 'interview', label: 'Interview scheduling', subject: 'Interview scheduling', text: 'Hello {{firstName}},\n\nPlease reply with your availability for an interview.' },
        { id: 'reminder', label: 'Missing-item reminder', subject: 'Your next steps', text: 'Hello {{firstName}},\n\nPlease review the outstanding items in your secure portal and contact HR if you need assistance.' },
    ],
    milestoneTypes: ['interview', 'follow-up', 'virtual-orientation', 'clinical-orientation', 'shadowing', 'first-day'].map((id) => ({ id, label: id.replaceAll('-', ' ') })),
    interviewers: defaultInterviewerOptions.map((row) => ({ ...row })),
};
export const milestoneDetailsSchema = z.object({
    type: title,
    title,
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime(),
    location: text,
    meetingUrl: httpsUrl.nullable().default(null),
    owner: title,
    candidateInstructions: text.default(''),
    notes: text.default(''),
}).strict().refine((v) => Date.parse(v.endsAt) > Date.parse(v.startsAt), { message: 'End must be after start', path: ['endsAt'] });
export const followUpDetailsSchema = z.object({
    title,
    assignee: title,
    assigneeId: z.string().trim().max(200).nullable().default(null),
    dueAt: z.string().datetime(),
    priority: z.enum(['normal', 'high', 'urgent']),
    notes: text.default(''),
}).strict();
const base = {
    id: z.string().min(1), personId: z.string().uuid(), docType: z.literal('hr-workflow'),
    revision: z.number().int().positive(), createdAt: z.string().datetime(), createdBy: z.string().min(1),
    updatedAt: z.string().datetime(), updatedBy: z.string().min(1),
};
export const offerRecordSchema = z.object({ ...base, kind: z.literal('offer'),
    candidateName: title,
    terms: offerTermsSchema, status: z.enum(['draft', 'reviewed', 'issued', 'withdrawn']),
    reviewedAt: z.string().datetime().nullable(), reviewedBy: z.string().nullable(),
    instanceId: z.string().nullable(),
}).strict();
export const milestoneRecordSchema = z.object({ ...base, kind: z.literal('milestone'),
    details: milestoneDetailsSchema, status: z.enum(['planned', 'completed', 'cancelled']),
    completedAt: z.string().datetime().nullable(),
}).strict();
export const hrWorkflowRecordSchema = z.discriminatedUnion('kind', [
    z.object({ ...base, kind: z.literal('settings'), settings: hrWorkflowSettingsSchema }).strict(),
    offerRecordSchema,
    /**
     * An offer signed outside the app (ADR-011 §4 Q4/Q5, #233). The signed letter is an UPLOADED,
     * scanned, committed file; a SharePoint link is only how rows written before 2026-09-24 point at
     * theirs, because ADR-009 retired SharePoint for new evidence.
     *
     * Both fields are nullable so a stored legacy row still parses, and the invariant — a record must
     * point at exactly one of them — is enforced at the WRITE boundary rather than here: a member of
     * `z.discriminatedUnion` has to be a plain object, and `.refine()` would make this a `ZodEffects`
     * and break the union. The command below requires `storedFileId` and accepts no URL at all, which
     * is the only path that creates one of these. A reader must handle both being null.
     */
    z.object({ ...base, kind: z.literal('existing-offer'),
        storedFileId: fileIdSchema.nullable().default(null),
        sourceUrl: personnelEvidenceUrlSchema.nullable().default(null),
        signedAt: z.string().datetime(), note: z.string().trim().min(1).max(4000),
    }).strict(),
    milestoneRecordSchema,
    z.object({ ...base, kind: z.literal('follow-up'), details: followUpDetailsSchema,
        status: z.enum(['open', 'resolved']), resolvedAt: z.string().datetime().nullable(), resolvedBy: z.string().nullable(),
    }).strict(),
    z.object({ ...base, kind: z.literal('comment'), text: z.string().trim().min(1).max(4000),
        mentions: z.array(z.object({ userId: title, name: title }).strict()).max(20).default([]),
    }).strict(),
    z.object({ ...base, kind: z.literal('message'), subject: title, text: z.string().trim().min(1).max(16000),
        to: z.string().email(), communicationId: z.string().min(1),
        relatedRecordId: z.string().nullable(),
    }).strict(),
]);
const command = { operationId: z.string().uuid() };
const update = { ...command, recordId: z.string().min(1), expectedRevision: z.number().int().positive() };
export const hrWorkflowCommandSchema = z.discriminatedUnion('action', [
    z.object({ ...command, action: z.literal('save-settings'), recordId: z.literal('hrw-settings'), expectedRevision: z.number().int().nonnegative(), settings: writableHrWorkflowSettingsSchema }).strict(),
    z.object({ ...command, action: z.literal('draft-offer'), terms: writableOfferTermsSchema }).strict(),
    z.object({ ...update, action: z.literal('edit-offer'), terms: writableOfferTermsSchema }).strict(),
    z.object({ ...update, action: z.literal('review-offer') }).strict(),
    z.object({ ...update, action: z.literal('issue-offer') }).strict(),
    z.object({ ...update, action: z.literal('withdraw-offer') }).strict(),
    /**
     * AN UPLOAD ID, NOT A FILE ID. #233's wording says the command takes `storedFileId`, but the client
     * never produces one: every existing path puts the upload id on the wire and the server derives the
     * file id with `storedFileIdFor(personId, uploadId)` — see `evidenceUploadId` on the credentials
     * path and its use in `direct-record.ts`. `storedFileId` appears in `apps/web` only as a read.
     *
     * `.strict()` and no `sourceUrl` member: a request carrying a link is refused as an unknown key,
     * which is what "new records cannot be created from a link" means in practice. Whether the upload
     * finished and passed the scan cannot be checked here — that needs the store, so the handler does
     * it before `nextRecord`.
     */
    z.object({ ...command, action: z.literal('record-existing-offer'), uploadId: evidenceUploadIdSchema,
        signedAt: z.string().datetime(), note: z.string().trim().min(1).max(4000),
    }).strict(),
    z.object({ ...command, action: z.literal('create-milestone'), details: milestoneDetailsSchema }).strict(),
    z.object({ ...update, action: z.literal('edit-milestone'), details: milestoneDetailsSchema }).strict(),
    z.object({ ...update, action: z.literal('complete-milestone'), completedAt: z.string().datetime() }).strict(),
    z.object({ ...update, action: z.literal('cancel-milestone') }).strict(),
    z.object({ ...update, action: z.literal('invite-milestone') }).strict(),
    z.object({ ...command, action: z.literal('create-follow-up'), details: followUpDetailsSchema }).strict(),
    z.object({ ...update, action: z.literal('edit-follow-up'), details: followUpDetailsSchema }).strict(),
    z.object({ ...update, action: z.literal('resolve-follow-up') }).strict(),
    z.object({ ...update, action: z.literal('reopen-follow-up') }).strict(),
    z.object({ ...command, action: z.literal('comment'), text: z.string().trim().min(1).max(4000),
        mentions: z.array(z.object({ userId: title, name: title }).strict()).max(20).default([]),
    }).strict(),
    // Composed email is not a workflow command: it queues through the outbox at
    // POST /api/people/{id}/communications (#62), never a synchronous send from this handler.
]);
export const hrWorkflowViewSchema = z.object({
    records: z.array(hrWorkflowRecordSchema),
    offerSteps: z.array(stepInstanceSchema),
    communications: z.array(communicationSchema),
    settings: hrWorkflowSettingsSchema,
    settingsRevision: z.number().int().nonnegative(),
    /**
     * When the person last entered `candidate` (ADR-011 Q7, #235), derived from the audit trail on
     * every read and never stored. The panel passes it to `acceptedOffer`, so what HR sees as
     * accepted is exactly what the onboarding gate counts. Null when the trail records no entry.
     */
    candidacySince: z.string().datetime().nullable().default(null),
    /**
     * The outcome of the offer email, on the ONE response that just sent it (#262).
     *
     * Null on every read and on every other command — it is what just happened, not state, which
     * is why it is not on the offer record. It carries no token and no access code, for the same
     * reason `deliveredPortalLinkSchema` carries none: those went to the candidate, and a copy
     * here would live in a browser tab and whatever a QA run captures.
     *
     * `delivered: false` with a `reason` is the frame's other path — the offer is issued and the
     * candidate has not been told, so HR is pointed at "Share the link by hand".
     */
    offerDelivery: z.object({
        delivered: z.boolean(),
        /** The masked destination, e.g. `j•••e` at the domain on file. Null when nothing was sent. */
        maskedTo: z.string().min(1).nullable(),
        /** Authored operational text, never a provider's or an exception's. */
        reason: z.string().max(200).nullable(),
    }).strict().nullable().default(null),
});
/**
 * The configured interviewers ON THEIR OWN (#341).
 *
 * Add candidate needs this list BEFORE the person exists, and every other way into agency settings
 * is `people/{id}/hr-workflow`, which 404s without a person. Just the one list rather than the whole
 * settings document, because a picker has no business receiving the agency's offer wording and
 * email templates in order to draw a dropdown.
 */
export const agencyInterviewersSchema = z.object({
    interviewers: z.array(interviewerOptionSchema),
}).strict();
/**
 * CHANGING the interviewer list from the screen that uses it (Paul, 2026-09-29; #372 part 4).
 *
 * Paul: "HR manages the list in the dropdown itself, with **+ Add** for a new interviewer and
 * **'Remove from list'**. Removing a name doesn't erase it from past interviews."
 *
 * NARROW COMMANDS, NOT A SETTINGS SAVE, and that is the whole point of this schema existing.
 * `save-settings` carries the ENTIRE agency settings document — offer templates, email templates,
 * compliance decisions, screening recurrences, the default offer file store. Reusing it from the
 * Add candidate form would mean a screen for typing a candidate's name could overwrite the agency's
 * compliance decisions on a stale read, and two HR users editing at once would clobber each other
 * on everything rather than merging two names. These two commands can touch one list and nothing
 * else, and the server applies them to whatever it currently holds.
 *
 * `operationId` for the same reason every other command has one: a double-click or a retried
 * request is one change, not two. It is the caller's half of the server's idempotency.
 *
 * REMOVAL IS NOT DELETION. A past interview stores its interviewers as `{ userId, name }` snapshots
 * on `person.intake`, and every reader renders the stored name — so taking somebody off the pick
 * list leaves every interview that named them intact. That is Paul's requirement and it is a
 * property of the data model rather than of this command, which is why it is pinned by a test.
 */
export const interviewerListCommandSchema = z.discriminatedUnion('action', [
    /** `id` is minted by the server: a client that could choose it could collide with an existing one. */
    z.object({ operationId: z.string().uuid(), action: z.literal('add'), label: title }).strict(),
    z.object({ operationId: z.string().uuid(), action: z.literal('remove'), id: title }).strict(),
]);
/**
 * The instant an EARLIER candidacy must be excluded from (ADR-011 §4, Q7, #235) — the latest
 * `person.lifecycle-changed` whose `to` is `candidate`: `withdrawn → candidate` (re-entry) and
 * `separated → candidate` (rehire) both write one.
 *
 * NULL FOR A FIRST CANDIDACY, and that is the whole point rather than a missing value. Null
 * means "no earlier candidacy to exclude", which every caller already reads it as
 * (`offerInCandidacy` short-circuits on it).
 *
 * It used to return the `person.created` instant for a first candidacy, and that broke the
 * ordinary paper-offer case: `record-existing-offer` is BY DEFINITION an offer signed outside
 * the app, so its `signedAt` routinely predates the person's record, and comparing an external
 * signing date against an internal creation timestamp refused it. HR then could not move the
 * candidate to onboarding, and was told "HR-recorded signed-offer evidence is required" while
 * that evidence was on file. A first candidacy has nothing before it to steal an offer from, so
 * there is nothing to compare against.
 *
 * Derived from the trail on every read; no field on Person stores it.
 */
export function candidacyStartedAt(events) {
    let latest = null;
    for (const event of events) {
        const reentry = event.action === 'person.lifecycle-changed' && event.to === 'candidate';
        if (reentry && (latest === null || Date.parse(event.occurredAt) > Date.parse(latest)))
            latest = event.occurredAt;
    }
    return latest;
}
/**
 * Did this offer become evidence during the current candidacy (Q7)?
 *
 * The moment that decides it is when the offer was ACCEPTED, not when it was first typed.
 * `nextRecord` carries `createdAt` through edit, review and issue, so a draft that was started
 * before a withdrawal and then issued, signed and approved after the person came back would
 * otherwise be judged by the old candidacy's date and refused for its own candidate.
 *
 * At or after the candidacy began counts. An offer from an earlier candidacy stays in history
 * and on the timeline; it just stops counting.
 */
export function offerInCandidacy(acceptedAt, candidacySince) {
    return candidacySince === null || Date.parse(acceptedAt) >= Date.parse(candidacySince);
}
/**
 * A portal-signed offer counts once HR has APPROVED its signed step — approval alone (ADR-011 Q2,
 * §4; #183). Approval is HR's review of the signed document; the PDF's commit follows by the
 * `store-file` effect, whose scan and hash gate ADR-009 §2 guarantees, and onboarding does not
 * wait on it. No file field is read: the retired `sharePointItemUrl` never arrived, which is why
 * this path could never count before.
 */
/**
 * WHEN this offer was accepted, or null if it has not been. The moment `acceptedOffer` judges
 * a candidacy by, and the only place that moment is defined.
 *
 * It is not on the record. An issued offer's `updatedAt` is frozen at the issue instant —
 * `nextRecord` refuses every command on a non-draft offer but `withdraw-offer` — so anything
 * reading `updatedAt` as the signature would report the day it went out.
 */
export function offerAcceptedAt(record, steps) {
    // A paper offer was accepted when it was SIGNED, which is what `signedAt` holds. `createdAt` is
    // when HR typed it in, and recording an old offer today is ordinary data entry — only a FUTURE
    // `signedAt` is refused at the command — so judging by `createdAt` would let an offer signed in
    // a previous candidacy unlock onboarding in this one.
    if (record.kind === 'existing-offer')
        return record.signedAt;
    if (record.kind !== 'offer' || record.status !== 'issued' || record.instanceId === null)
        return null;
    const signed = steps.find((step) => step.id === record.instanceId && step.personId === record.personId &&
        step.state === 'approved');
    if (signed === undefined)
        return null;
    // A portal offer is accepted when HR approves the signed step. `reviewedAt` is that moment;
    // the record's own review time, then its last write, stand in for a document written before
    // the step carried one.
    return signed.reviewedAt ?? record.reviewedAt ?? record.updatedAt;
}
/**
 * `2026-08-01` → 20260801, so two days compare as NUMBERS rather than as text.
 *
 * WHAT THIS DOES AND DOES NOT FIX, stated precisely because the commit that introduced it got the
 * attribution wrong. The year-300 unlock — a paper offer dated `0300-01-01` counting after a 2026
 * re-entry — is closed by `paperSignedOn`, NOT by this function: an ISO date prefix is always
 * zero-padded, and `'0300-01-01' >= '2026-08-01'` is already false as text. Deleting `dayOrdinal`
 * leaves every one of the suite's tests passing.
 *
 * What it guards is the OTHER side. `agencyCalendarDate` formats with
 * `Intl.DateTimeFormat('en-CA', { year: 'numeric' })`, which does not zero-pad a year below 1000 —
 * it answers `"299-12-31"` — so a `candidacySince` with such a year would make a text comparison
 * refuse a valid modern offer. That direction is fail-closed rather than a security hole, and it
 * needs a sub-1000 `occurredAt` on a server-generated audit row, which is why nothing in production
 * reaches it. Splitting on `-` is indifferent to how wide the year is printed, so it is correct
 * either way, and `tests/offer-candidacy.test.ts` now pins this direction specifically.
 */
function dayOrdinal(calendarDate) {
    const [year, month, day] = calendarDate.split('-').map(Number);
    return year * 10000 + month * 100 + day;
}
/**
 * The calendar date a PAPER offer was signed on, as HR typed it.
 *
 * The ISO date prefix, not the agency day of the instant — and that is deliberate, because it is
 * the one reading that is right for every format this field has ever held. `z.string().datetime()`
 * forces UTC with `Z` (offsets and a lowercase `z` are both rejected), and every value the form has
 * ever written is the start of the typed day in the agency zone, which is behind UTC — so the prefix
 * is the typed day:
 *
 *   the form before 2026-09-23:  `2026-08-01T00:00:00.000Z` → 2026-08-01 ✓
 *   the form after it:           `2026-08-01T04:00:00.000Z` → 2026-08-01 ✓
 *
 * Reading the agency day instead would answer 2026-07-31 for every row already in the database,
 * because midnight UTC is 8pm the previous evening in Virginia — so the fix for one format would
 * have silently shifted every stored paper offer back a day. This is also exactly what
 * `formatRecordedCalendarDate` shows on screen, so what HR reads and what the gate decides cannot
 * disagree about the same record.
 *
 * NOT AN ENFORCED INVARIANT. The form is the only writer in this repo, but the command accepts any
 * `z.string().datetime()` that is not in the future, so a non-panel client could store a real
 * instant whose UTC prefix falls on a different agency day (`2026-08-02T01:00:00.000Z` reads as
 * 2026-08-02 here and 2026-08-01 in the agency zone). Both readings are defensible for an arbitrary
 * instant, and this one at least agrees with what the screen shows. Requiring
 * `signedAt === startOfDayInAgencyZone(signedAt.slice(0, 10))` at the command would make it an
 * invariant; that is a separate decision about the command's contract.
 */
function paperSignedOn(signedAt) {
    return signedAt.slice(0, 10);
}
/**
 * Whether an ACCEPTED offer's acceptance falls inside the current candidacy — the one definition,
 * so the onboarding gate and the Offers list can never disagree about the same offer.
 *
 * A PAPER OFFER IS COMPARED BY DAY, not by instant, because `signedAt` came from a date field:
 * HR types "signed Sep 23" and no clock time exists. Comparing a manufactured midnight against a
 * real timestamp refused an offer signed the same day a candidate re-entered, since midnight is
 * earlier than any hour of that day. Day granularity is the precision the datum actually has.
 *
 * A portal offer keeps instant granularity: its acceptance is a real recorded moment (HR approving
 * the signed step), so the same day is not good enough — an offer approved at 09:00 and a re-entry
 * at 14:00 the same day are genuinely in that order.
 */
export function acceptedInCandidacy(record, acceptedAt, candidacySince) {
    if (candidacySince === null)
        return true;
    if (record.kind !== 'existing-offer')
        return offerInCandidacy(acceptedAt, candidacySince);
    // The offer's own typed date; the candidacy's start is a real instant, so it becomes the day it
    // fell on where the agency operates.
    return dayOrdinal(paperSignedOn(acceptedAt)) >= dayOrdinal(agencyCalendarDate(candidacySince));
}
export function acceptedOffer(record, steps, 
/**
 * From `candidacyStartedAt`. Required, not defaulted, so no caller can forget the Q7 scope and
 * quietly count an offer from before a withdrawal or separation.
 */
candidacySince) {
    const acceptedAt = offerAcceptedAt(record, steps);
    return acceptedAt !== null && acceptedInCandidacy(record, acceptedAt, candidacySince);
}
/**
 * THE accepted offer when a person has more than one: the LATEST by acceptance time (Julio,
 * 2026-10-03). Every screen that shows or starts from "the signed offer" reads this, so two of
 * them cannot pick different offers.
 *
 * The time is `offerAcceptedAt`: for a portal offer the moment HR approved the signed step, for a
 * paper offer its typed `signedAt`. Not `createdAt` — an offer drafted earlier can be signed later.
 * On an equal time the later record in the list wins, so the answer does not depend on a sort.
 *
 * The onboarding gate does not call this: it asks whether ANY offer is accepted, which has no order.
 */
export function latestAcceptedOffer(records, steps, candidacySince) {
    let latest = null;
    let latestAt = Number.NEGATIVE_INFINITY;
    for (const record of records) {
        if (record.kind !== 'offer' && record.kind !== 'existing-offer')
            continue;
        const acceptedAt = offerAcceptedAt(record, steps);
        if (acceptedAt === null || !acceptedInCandidacy(record, acceptedAt, candidacySince))
            continue;
        const at = Date.parse(acceptedAt);
        if (at >= latestAt) {
            latest = record;
            latestAt = at;
        }
    }
    return latest;
}
/** Exactly the offered facts, also used for HR preview; never read mutable Person fields after issue. */
export function offerTermsText(terms, candidateName) {
    return [`Offer for ${candidateName}`, `Position: ${terms.position}`, `Department: ${terms.department}`,
        `Classification: ${terms.employmentClassification === '1099' ? '1099 contractor' : 'W-2 employee'}`,
        `Employment type: ${terms.employmentType}`, `Service area: ${terms.serviceArea}`,
        `Proposed start date: ${terms.proposedStartDate}`, `Accept by: ${terms.acceptanceDeadline}`,
        'Compensation:', ...terms.compensation.map((row) => `${row.description}: USD ${row.amount.toFixed(2)} per ${row.unit}${row.details ? ` - ${row.details}` : ''}`),
        '',
        // Says only what is true. When HR uploaded the letter instead of typing wording, this
        // artifact is still the acceptance instrument — it does NOT embed the uploaded PDF — so it
        // states that the letter is recorded, and claims nothing about what was signed (#260).
        terms.termsText ?? 'The approved offer letter was uploaded with this offer and is recorded against it.',
    ].join('\n');
}
//# sourceMappingURL=model.js.map